/**
 * Admin Retailer Members API client — talks to /api/admin-retailer-members.php
 * (the approval queue for retailer-staff accounts + invite campaigns).
 */

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export type MemberStatus = 'pending' | 'approved' | 'rejected' | 'unmatched';
/** viewer | manager. Both apply across the user's location set (or every location with the all-locations switch). */
export type RetailerMemberRole = 'viewer' | 'manager';
export interface StoreOption { id: number; name: string; city: string | null; retailerId: number }
export interface MemberLocation { id: number; name: string; city: string | null }
export type MemberFilter = 'open' | MemberStatus | 'all';

export interface RetailerMemberRow {
  id: number;
  userId: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  retailerId: number | null;
  retailerName: string | null;
  role: RetailerMemberRole;
  status: MemberStatus;
  /** salesforce | salesforce_parent | domain | invite | self | admin */
  source: string;
  sfContactId: string | null;
  sfAccountId: string | null;
  sfParentAccountId: string | null;
  sfAccountName: string | null;
  /** True when the row's Salesforce Account is already mapped to its retailer. */
  sfAccountMapped: boolean;
  /** The whole node: every location of the retailer, incl. ones added later, plus its portal estimates. */
  allLocations: boolean;
  /** With allLocations on a top-level retailer: its sub-retailers too. */
  includeChildren: boolean;
  /** The locations this person holds (seeded from Salesforce, then edited by people). */
  locations: MemberLocation[];
  /** Live locations of the row's retailer. */
  locationCount: number;
  /** all = the whole node · locations = a set of stores · none = a set row whose stores all left (fix it). */
  scope: 'all' | 'locations' | 'none';
  /** Contact.viewEstimates__c / manageEstimates__c as last read; null = unknown. */
  sfViewEstimates: boolean | null;
  sfManageEstimates: boolean | null;
  /** 'salesforce' (seeded from the flags) | 'local' (set by a person) | 'default'. */
  roleSource: 'salesforce' | 'local' | 'default';
  roleSetBy: string | null;
  roleSetAt: string | null;
  inviteCode: string | null;
  matchedDomain: string | null;
  /** Other retailers that also matched (for the approver to pick from). */
  candidates: Array<{ id: number; name: string }>;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RetailerOption { id: number; name: string; isActive: boolean }

export interface MembersListResponse {
  members: RetailerMemberRow[];
  counts: Record<MemberStatus, number>;
  retailers: RetailerOption[];
  /** Every live store linked to a retailer, for the per-row Store picker. */
  stores?: StoreOption[];
  warning?: string;
}

export interface InviteCampaign {
  id: number;
  code: string;
  retailerId: number | null;
  retailerName: string | null;
  label: string | null;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
}

export interface InviteCampaignInput {
  id?: number;
  code: string;
  retailerId: number | null;
  label: string | null;
  isActive: boolean;
  expiresAt: string | null;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

async function post<T>(body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}/admin-retailer-members.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handle<T>(res);
}

export async function fetchRetailerMembers(filter: MemberFilter, q = '', retailerId: number | null = null): Promise<MembersListResponse> {
  const params = new URLSearchParams({ status: filter });
  if (q.trim()) params.set('q', q.trim());
  if (retailerId) params.set('retailer_id', String(retailerId));
  const res = await fetch(`${API_BASE}/admin-retailer-members.php?${params}`, { credentials: 'include' });
  return handle<MembersListResponse>(res);
}

export async function fetchOpenMemberCount(): Promise<number> {
  try {
    const d = await fetchRetailerMembers('open');
    return d.counts.pending + d.counts.unmatched;
  } catch {
    return 0;
  }
}

export type MemberAction =
  | { action: 'approve'; id: number; retailerId?: number; rememberSfAccount?: boolean; locationIds?: number[]; allLocations?: boolean }
  | { action: 'link'; id: number; retailerId: number; rememberSfAccount?: boolean }
  | { action: 'reject'; id: number }
  | { action: 'set_role'; id: number; role: RetailerMemberRole }
  | { action: 'set_locations'; id: number; locationIds: number[]; allLocations: boolean }
  | { action: 'set_access'; id: number; role: RetailerMemberRole; grants: Array<{ type: 'retailer'; id: number; includeChildren?: boolean } | { type: 'location'; id: number }> }
  | { action: 'seed_location'; id: number }
  | { action: 'note'; id: number; notes: string };

export async function memberAction(body: MemberAction): Promise<{ member: RetailerMemberRow | null }> {
  return post(body);
}

/** Approve several waiting rows at once; a row with nothing picked gets the whole node. */
export async function approveMembers(ids: number[]): Promise<{ members: RetailerMemberRow[]; skipped: { id: number; reason: string }[] }> {
  return post({ action: 'approve_many', ids });
}

export async function deleteMember(id: number): Promise<{ deleted: boolean }> {
  return post({ action: 'delete', id });
}

export async function reclassifyMember(id: number): Promise<{ result: { ran: boolean; rows: number; reason: string }; members: RetailerMemberRow[] }> {
  return post({ action: 'reclassify', id });
}

export async function fetchInviteCampaigns(): Promise<{ campaigns: InviteCampaign[]; retailers: RetailerOption[] }> {
  const res = await fetch(`${API_BASE}/admin-retailer-members.php?resource=campaigns`, { credentials: 'include' });
  return handle(res);
}

export async function saveInviteCampaign(campaign: InviteCampaignInput): Promise<{ campaigns: InviteCampaign[] }> {
  return post({ action: 'campaign_save', campaign });
}

export async function deleteInviteCampaign(id: number): Promise<{ campaigns: InviteCampaign[] }> {
  return post({ action: 'campaign_delete', id });
}
